import * as vp from 'vopay';
import { FunctionComponent, useContext, useState, useEffect } from 'react';

import BillingPackageCreationContext from './BillingPackageCreationContext';

import MultiStepSection from './MultiStepSection';
import MultiStepReview from './MultiStepReview';
import { useTranslation } from 'react-i18next';

const { MultiStep } = vp;

type FormProps = {
  isComplete: boolean;
};

const BillingPackageCreationForm: FunctionComponent<FormProps> = ({ isComplete = false }: FormProps) => {
  const ctx = useContext(BillingPackageCreationContext);
  const { getCompletionPercentage, hasError } = ctx.fields;
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation('translations', { keyPrefix: 'BillingPackage.Creation' });
  const { t: tForm } = useTranslation('translations', { keyPrefix: 'BillingPackageCreationForm' });

  const handleStepChange = (stepIdx: number) => {
    setCurrentStep(stepIdx);
  };
  useEffect(() => {
    if (ctx.values.iQ11StandardEnabled) {
      ctx.handleFieldChange(vp.createSyntheticEvent('iQ11TokenEnabled', true));
      ctx.handleFieldChange(vp.createSyntheticEvent('iQ11ThirdPartyEnabled', true));
    } else {
      ctx.handleFieldChange(vp.createSyntheticEvent('iQ11TokenEnabled', false));
      ctx.handleFieldChange(vp.createSyntheticEvent('iQ11ThirdPartyEnabled', false));
    }
  }, [ctx.values.iQ11StandardEnabled]);
  const steps = [
    {
      label: t('PackageSettingsStep.title'),
      content: <MultiStepSection sectionId="packageInfo" />,
      showStep: () => true,
      hasError: () => hasError('packageInfo', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('packageInfo', ctx, currentStep >= idx),
    },
    {
      label: tForm('Steps.billingSettings', { defaultValue: 'Billing Settings' }),
      content: <MultiStepSection sectionId="billingInfo" />,
      showStep: () => true,
      hasError: () => hasError('billingInfo', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('billingInfo', ctx, currentStep >= idx),
    },
    {
      label: tForm('Steps.planSettings', { defaultValue: 'Plan Settings' }),
      content: <MultiStepSection sectionId="subscriptionInfo" showSubLabels />,
      showStep: () => true,
      hasError: () => hasError('subscriptionInfo', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('subscriptionInfo', ctx, currentStep >= idx),
    },
    {
      label: tForm('Steps.reviewAndSubmit', { defaultValue: 'Review and Submit' }),
      content: <MultiStepReview />,
      showStep: () => true,
      hasError: () => false,
      percentageComplete: () => (isComplete ? 100 : 0),
    },
  ];

  return (
    <MultiStep
      id="create-billing-package"
      title={tForm('title', { defaultValue: 'Add Billing Package' })}
      steps={steps}
      onStepChange={handleStepChange}
    />
  );
};

export default BillingPackageCreationForm;
